import React from "react";
import { Icon } from "@/components/Icon";
import { Button } from "@/components/Button";

export interface PageHeaderActionConfig {
    label: string;
    icon?: string;
    onClick: () => void;
    permissionKey?: string;
}

export interface PageHeaderProps {
    icon?: string;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    action?: React.ReactNode | PageHeaderActionConfig;
    className?: string;
    compact?: boolean;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
    icon,
    title,
    subtitle,
    action,
    className = "mb-4",
    compact = false,
}) => {
    const isActionConfig =
        action &&
        typeof action === "object" &&
        "label" in action &&
        "onClick" in action;
    const actionConfig = isActionConfig
        ? (action as PageHeaderActionConfig)
        : null;
    return (
        <div
            className={`${compact ? "pt-1 pb-3" : "bg-box rounded-xl border border-slate-200/80 p-6 shadow-xs"} flex flex-col md:flex-row md:items-center justify-between gap-3 ${className}`}
        >
            <div>
                <h1 className="gap-2 text-2xl font-bold text-slate-900 dark:text-zinc-100 tracking-tight flex items-center">
                    {icon ? <Icon value={icon} /> : ""}
                    {title}
                </h1>
                {subtitle && (
                    <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">
                        {subtitle}
                    </p>
                )}
            </div>

            {action && (
                <div className="flex-shrink-0 w-full md:w-auto">
                    {actionConfig ? (
                        <Button
                            variant="primary"
                            onClick={actionConfig.onClick}
                            permissionKey={actionConfig.permissionKey}
                            icon={
                                actionConfig.icon === "plusCircle"
                                    ? "plus"
                                    : actionConfig.icon
                            }
                            className="w-full md:w-auto"
                        >
                            {actionConfig.label}
                        </Button>
                    ) : (
                        (action as React.ReactNode)
                    )}
                </div>
            )}
        </div>
    );
};

export default PageHeader;
